import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { getToken, clearToken } from '../utils/tokenStorage';

// Cache auth state to prevent repeated checks
let cachedAuthState = null;
let lastAuthCheck = 0;
const AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function ProtectedRoute({ children }) {
  const location = useLocation();
  const setUser = useAuthStore((state) => state.setUser);
  const setLoading = useAuthStore((state) => state.setLoading);
  const storeUser = useAuthStore((state) => state.user);
  const storeIsAuth = useAuthStore((state) => state.isAuthenticated);

  // Trust persisted Zustand store on initial load when cachedAuthState is null
  const getInitialAuth = () => {
    if (location.state?.user) return true;
    if (cachedAuthState !== null) return cachedAuthState;
    if (storeIsAuth && storeUser && getToken()) return true;
    return null;
  };

  const [isAuthenticated, setIsAuthenticated] = useState(getInitialAuth);
  const checkInProgress = useRef(false);

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setLoading(false);
      cachedAuthState = true;
      lastAuthCheck = Date.now();
      return;
    }
    
    // If we already have a valid cached auth state, use it
    const now = Date.now();
    if (cachedAuthState === true && (now - lastAuthCheck) < AUTH_CACHE_DURATION) {
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }
    
    // Check if token exists - if not, definitely not authenticated
    const token = getToken();
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      cachedAuthState = false;
      return;
    }
    
    // If we have persisted auth state and haven't verified yet, allow access while checking
    if (storeIsAuth && storeUser) {
      setIsAuthenticated(true);
      setLoading(false);
    }
    
    // Prevent concurrent auth checks
    if (checkInProgress.current) return;

    const checkAuth = async () => {
      checkInProgress.current = true;
      
      try {
        // Try auth check with retry for resilience
        let response;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            response = await apiClient.get('/auth/me');
            break;
          } catch (retryErr) {
            if (attempt < 2 && !retryErr.response) {
              // Network error - wait and retry
              await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }
            throw retryErr;
          }
        }
        setUser(response.data);
        setIsAuthenticated(true);
        cachedAuthState = true;
        lastAuthCheck = Date.now();
      } catch (error) {
        // Only clear auth if it's a definitive 401 error
        if (error.response?.status === 401) {
          setIsAuthenticated(false);
          cachedAuthState = false;
          clearToken();
        } else {
          // Network error - trust local state if we have a token and persisted user
          const hasToken = !!getToken();
          if (hasToken && (storeUser || cachedAuthState === true)) {
            setIsAuthenticated(true);
            cachedAuthState = true;
          } else {
            setIsAuthenticated(false);
          }
        }
      } finally {
        setLoading(false);
        checkInProgress.current = false;
      }
    };

    checkAuth();
  }, [location.pathname, setUser, setLoading, storeUser, storeIsAuth]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-default rounded-2xl p-8">
          <div className="animate-pulse text-center">
            <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-lg text-white/70">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = `/login?redirect=${encodeURIComponent(location.pathname + (location.search || ''))}`;
    return null;
  }

  return children;
}
